import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPage } from '../lib/cms';
import { useScrollReveal } from '../hooks/useScrollReveal';

export default function About() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      const data = await fetchPage('page-om-os');
      setPageData(data);
      setLoading(false);
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title || 'Om os - JE Entreprenør';
  const metaDesc = pageData?.meta_description || 'Læs mere om JE Entreprenør – din lokale entreprenør på Sydfyn.';
  const heroTitle = pageData?.hero_title || 'Om JE Entreprenør';
  const heroSubtitle = pageData?.hero_subtitle || 'Solidt håndværk, lokal forankring og professionelt entreprenørarbejde på Sydfyn.';

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/om-us-entreprenoer.jpg" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-asphalt-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-30">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/om-us-entreprenoer.jpg"
            alt="JE Entreprenør om os hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-asphalt-950/80" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
            {heroTitle}
          </h1>
          <p className="mt-4 mx-auto max-w-2xl text-base text-asphalt-200">
            {heroSubtitle}
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section 
        className="py-16 bg-white"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="prose prose-asphalt lg:col-span-7">
              <h2 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-asphalt-950 mb-6">
                Solidt entreprenørarbejde bygget på tillid
              </h2>
              <div 
                className="text-base text-asphalt-700 space-y-6 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: pageData?.content || '<p>Indlæser...</p>' }}
              />
            </div>
            <div className="lg:col-span-5">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/ca189e60-8ca7-4520-a859-93fb9daa9500/mandskabsudlejning.jpg"
                alt="JE Entreprenør professionelt mandskab"
                className="w-full h-[450px] object-cover border border-black shadow-lg"
              />
            </div>
          </div>

          {/* Company Values */}
          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
            <div className="border border-asphalt-200 bg-white p-6 text-center">
              <div className="mx-auto flex items-center justify-center text-safety-orange">
                <i className="fa-solid fa-handshake text-2xl"></i>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-asphalt-900">Troværdighed</h3>
              <p className="mt-2 text-sm text-asphalt-600">
                En aftale er en aftale. Vi leverer til tiden og til den aftalte pris uden ubehagelige overraskelser.
              </p>
            </div>

            <div className="border border-asphalt-200 bg-white p-6 text-center">
              <div className="mx-auto flex items-center justify-center text-safety-orange">
                <i className="fa-solid fa-award text-2xl"></i>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-asphalt-900">Højt Kvalitetsniveau</h3>
              <p className="mt-2 text-sm text-asphalt-600">
                Vi går aldrig på kompromis med materialer eller udførelse. Vores arbejde skal holde i many år.
              </p>
            </div>

            <div className="border border-asphalt-200 bg-white p-6 text-center">
              <div className="mx-auto flex items-center justify-center text-safety-orange">
                <i className="fa-solid fa-comments text-2xl"></i>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-asphalt-900">Tæt Dialog</h3>
              <p className="mt-2 text-sm text-asphalt-600">
                Vi holder dig løbende orienteret gennem hele processen, så du altid føler dig tryg og velinformeret.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Local Connection Banner */}
      <section 
        className="bg-sand-100 py-16 border-t border-b border-asphalt-100"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-asphalt-900 sm:text-3xl">
            Sydfynsk forankring med base i Skårup
          </h2>
          <p className="mt-4 mx-auto max-w-2xl text-base text-asphalt-600 leading-relaxed">
            Som lokal entreprenør på Sydfyn sætter vi en ære i at støtte og bidrage til lokalsamfundet. Vi kender jordbundsforholdene, de lokale regler og samarbejder med andre dygtige lokale håndværkere og leverandører.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-8 text-sm font-bold text-asphalt-700">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-circle text-[8px] text-safety-orange"></i>
              <span>Hurtig udrykning</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-circle text-[8px] text-safety-orange"></i>
              <span>Personlig kontakt</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-circle text-[8px] text-safety-orange"></i>
              <span>Lokale samarbejdspartnere</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
